import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Bed, Search, User, ChevronRight, Loader2, Users } from "lucide-react";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { useOssAuth } from "../../context/OssAuthContext";
import OssSectionPageHeader from "../../components/oss/OssSectionPageHeader";
import OssTerapiaAlertBadge from "../../components/oss/OssTerapiaAlertBadge";
import { formatCameraAssegnata } from "../../utils/formatCamera";

const API_URL = process.env.REACT_APP_BACKEND_URL;

function getSessoAvatarStyles(sesso) {
  const raw = (sesso ?? "").toString().trim();
  const u = raw.toUpperCase();
  const lower = raw.toLowerCase();
  const femmina = u === "F" || u === "FEMMINA" || lower === "femmina";
  const maschio = u === "M" || u === "MASCHIO" || lower === "maschio";
  if (femmina) {
    return {
      fotoRing: "ring-2 ring-pink-400 ring-offset-2 border-transparent",
      placeholderBg: "bg-gradient-to-br from-pink-50 to-rose-100 border-pink-200/90",
      iconClass: "text-pink-600",
    };
  }
  if (maschio) {
    return {
      fotoRing: "ring-2 ring-blue-500 ring-offset-2 border-transparent",
      placeholderBg: "bg-gradient-to-br from-sky-50 to-blue-100 border-blue-200/90",
      iconClass: "text-blue-600",
    };
  }
  return {
    fotoRing: "border border-slate-200",
    placeholderBg: "bg-gradient-to-br from-slate-100 to-slate-200 border-slate-200/80",
    iconClass: "text-slate-500",
  };
}

function OspitePreview({ ospite, large = false }) {
  const av = getSessoAvatarStyles(ospite?.sesso);
  const size = large
    ? "w-20 h-20 sm:w-24 sm:h-24"
    : "w-16 h-16 sm:w-20 sm:h-20";
  const iconSize = large ? "w-10 h-10 sm:w-12 sm:h-12" : "w-8 h-8 sm:w-10 sm:h-10";
  const name = ospite?.nome_completo || "Ospite";

  return (
    <div className="flex flex-col items-center text-center min-w-0 w-full">
      {ospite?.foto_url ? (
        <img
          src={ospite.foto_url}
          alt=""
          className={`${size} rounded-full object-cover shrink-0 ${av.fotoRing}`}
        />
      ) : (
        <div
          className={`${size} rounded-full flex items-center justify-center shrink-0 border ${av.placeholderBg}`}
        >
          <User className={`${iconSize} ${av.iconClass}`} />
        </div>
      )}
      <p
        className={`mt-3 font-bold text-slate-900 leading-tight w-full ${
          large ? "text-xl sm:text-2xl" : "text-lg sm:text-xl"
        }`}
        title={name}
      >
        <span className="block truncate">{ospite?.cognome || name}</span>
        {ospite?.nome ? (
          <span className="block truncate font-semibold text-slate-700 text-[0.85em] mt-0.5">
            {ospite.nome}
          </span>
        ) : null}
      </p>
    </div>
  );
}

export default function OssCamere() {
  const { token } = useOssAuth();
  const navigate = useNavigate();
  const [camere, setCamere] = useState([]);
  const [loading, setLoading] = useState(true);
  const searchInputRef = useRef(null);
  const [searchApplied, setSearchApplied] = useState("");

  useEffect(() => {
    if (!token) return;
    axios
      .get(`${API_URL}/api/oss/me/camere`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(({ data }) => setCamere(Array.isArray(data) ? data : []))
      .catch(() => setCamere([]))
      .finally(() => setLoading(false));
  }, [token]);

  const applySearch = () => {
    setSearchApplied((searchInputRef.current?.value ?? "").trim().toLowerCase());
  };

  const filtered = camere.filter((c) => {
    const q = searchApplied;
    if (!q) return true;
    const name = (c.name || "").toLowerCase();
    const piano = (c.piano || "").toLowerCase();
    const ospitiMatch = (c.ospiti || []).some((o) =>
      (o.nome_completo || "").toLowerCase().includes(q)
    );
    return name.includes(q) || piano.includes(q) || ospitiMatch;
  });

  return (
    <div className="max-w-6xl mx-auto">
      <OssSectionPageHeader
        sectionKey="camere"
        title="Camere"
        subtitle={
          !loading && camere.length > 0 ? (
            <span className="tabular-nums">
              {filtered.length === camere.length ? (
                <span>
                  {camere.length} camer{camere.length === 1 ? "a" : "e"}
                </span>
              ) : (
                <span>
                  {filtered.length} di {camere.length} risultat
                  {filtered.length === 1 ? "o" : "i"}
                </span>
              )}
            </span>
          ) : (
            "Ospiti per camera — tocca una card per il dettaglio"
          )
        }
        toolbar={
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
            <div className="relative flex-1 min-w-0">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
              <Input
                ref={searchInputRef}
                placeholder="Cerca camera o ospite…"
                defaultValue=""
                onBlur={applySearch}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    applySearch();
                  }
                }}
                className="pl-9 h-11 w-full text-base bg-white/80"
              />
            </div>
            <Button
              type="button"
              variant="secondary"
              className="shrink-0 sm:w-auto w-full border-slate-200 min-h-[44px] touch-manipulation"
              onClick={applySearch}
            >
              Applica filtro
            </Button>
          </div>
        }
      />

      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="w-8 h-8 animate-spin text-teal-500" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-slate-500">
          <Bed className="w-12 h-12 mx-auto mb-3 text-slate-300" />
          <p>
            {searchApplied
              ? "Nessun risultato per la ricerca"
              : "Nessuna camera configurata per la struttura"}
          </p>
        </div>
      ) : (
        <ul
          className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-5"
          role="list"
        >
          {filtered.map((camera) => {
            const ospiti = camera.ospiti || [];
            const label = formatCameraAssegnata(camera.name) || camera.name || "Camera";
            const multi = ospiti.length > 1;

            const ospitiConAlert = ospiti
              .filter((o) => (Number(o.alert_terapie) || 0) > 0)
              .slice(0, 3);
            const openCamera = () => navigate(`/oss/camere/${camera.id}`);

            return (
              <li key={camera.id} className="h-full">
                <div className="group h-full w-full oss-tile bg-white hover:border-teal-400 transition-all flex flex-col overflow-hidden">
                  <div className="flex items-start justify-between gap-3 px-4 sm:px-5 pt-4 pb-2">
                    <h2 className="min-w-0 text-lg sm:text-xl font-bold text-teal-800 leading-tight tracking-tight">
                      {label}
                    </h2>
                    <div className="flex flex-wrap items-center justify-end gap-1.5 shrink-0 max-w-[55%]">
                      {camera.type ? (
                        <Badge
                          variant="outline"
                          className="text-[11px] font-normal border-slate-200 bg-slate-50"
                        >
                          {camera.type}
                        </Badge>
                      ) : null}
                      {camera.piano ? (
                        <Badge
                          variant="outline"
                          className="text-[11px] font-normal border-slate-200 bg-slate-50"
                        >
                          Piano {camera.piano}
                        </Badge>
                      ) : null}
                      <OssTerapiaAlertBadge
                        count={camera.alert_terapie}
                        overdue={camera.alert_terapie_overdue}
                      />
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={openCamera}
                    className="flex-1 flex items-start justify-center px-4 sm:px-5 pt-1 pb-2 min-h-[140px] text-left touch-manipulation focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-teal-500"
                  >
                    {ospiti.length === 0 ? (
                      <div className="flex flex-col items-center text-center text-slate-400 py-6 w-full">
                        <Users className="w-10 h-10 mb-2 opacity-50" />
                        <p className="text-sm font-medium">Nessun ospite assegnato</p>
                      </div>
                    ) : multi ? (
                      <div className="grid grid-cols-2 gap-4 w-full">
                        {ospiti.slice(0, 2).map((o) => (
                          <OspitePreview key={o.id} ospite={o} />
                        ))}
                      </div>
                    ) : (
                      <OspitePreview ospite={ospiti[0]} large />
                    )}
                  </button>

                  {ospiti.length > 2 ? (
                    <p className="text-xs text-center text-slate-500 tabular-nums px-4 -mt-1 mb-1">
                      +{ospiti.length - 2} altr{ospiti.length - 2 === 1 ? "o" : "i"} ospite
                      {ospiti.length - 2 === 1 ? "" : "i"}
                    </p>
                  ) : null}

                  <div className="mt-auto border-t border-slate-100 px-3 sm:px-4 pt-2.5 pb-3 flex flex-col gap-2">
                    <div className="min-h-[24px] flex flex-wrap gap-1.5 justify-center items-center">
                      {ospitiConAlert.map((o) => (
                        <OssTerapiaAlertBadge
                          key={o.id}
                          count={o.alert_terapie}
                          overdue={o.alert_terapie_overdue}
                          label={(o.cognome || o.nome || "Ospite").slice(0, 12)}
                        />
                      ))}
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={openCamera}
                      className="w-full h-11 rounded-xl border-teal-200 text-teal-800 hover:bg-teal-50 hover:border-teal-300 touch-manipulation"
                    >
                      Visualizza
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
